import { supabase } from './supabase.js';
import { getSessionUser } from './auth.js';

/**
 * Place an order. Pricing is enforced by the database — see
 * supabase/migrate_order_pricing.sql. The client sends only `productId`
 * and `quantity` per line; `unit_price`, `line_total`, `subtotal`,
 * `shipping`, and `total` are computed by BEFORE/AFTER INSERT triggers
 * from products.price.
 *
 * `lines` shape: [{ productId, quantity }]
 *   (legacy callers may also pass name / weight / unitPrice — those are
 *    ignored server-side, kept for backwards compatibility only.)
 *
 * Returns { order, error }. `order` is the row AFTER the triggers fill
 * in real subtotal/shipping/total, so the UI can display the trusted
 * numbers without recomputing.
 */
export async function placeOrder({
  fullName, phone, email,
  addressLine1, addressLine2, city, state, pincode,
  notes,
  lines,
  couponCode,
}) {
  if (!lines || lines.length === 0) return { error: new Error('Cart is empty') };

  const user = await getSessionUser();
  if (!user) {
    return {
      error: new Error('Your session expired. Please sign in again to place your order.'),
      authRequired: true,
    };
  }

  // 1. Insert the parent order. The `orders_zero_pricing` trigger blanks
  //    subtotal/shipping/total/discount; we still send placeholder zeros
  //    to satisfy NOT NULL constraints. `coupon_code` is the only signal
  //    the trigger needs to look up + re-validate the coupon server-side.
  const { data: order, error: orderErr } = await supabase
    .from('orders')
    .insert({
      user_id: user.id,
      status: 'pending',
      full_name: fullName,
      phone,
      email: email ?? user.email ?? null,
      address_line1: addressLine1,
      address_line2: addressLine2,
      city,
      state,
      pincode,
      subtotal: 0,
      shipping: 0,
      total: 0,
      notes: notes ?? null,
      payment_method: 'razorpay',
      payment_status: 'pending',
      coupon_code: couponCode && couponCode.trim() ? couponCode.trim() : null,
    })
    .select()
    .single();
  if (orderErr) return { error: orderErr };

  // 2. Insert the line items. The `order_items_enforce_pricing` trigger
  //    overwrites unit_price + line_total from products.price (or from the
  //    selected variant when variant_id is set); the `orders_recompute_totals`
  //    trigger then fills the order row.
  const itemsPayload = lines.map(l => ({
    order_id: order.id,
    product_id: l.productId,
    variant_id: l.variantId || null,
    quantity: l.quantity,
    // unit_price + line_total are required NOT NULL columns; placeholders
    // satisfy the constraint and the trigger immediately overwrites them.
    unit_price: 0,
    line_total: 0,
    // product_name is also NOT NULL. The trigger fills it from products.name
    // if we leave it blank, but Postgres needs a non-null value at insert
    // time — pass a placeholder.
    product_name: '',
  }));

  const { error: itemsErr } = await supabase.from('order_items').insert(itemsPayload);
  if (itemsErr) {
    // Best-effort rollback: delete the order row we just made.
    await supabase.from('orders').delete().eq('id', order.id);
    return { error: itemsErr };
  }

  // 3. Re-fetch the order so the caller sees the trigger-computed totals.
  const { data: finalOrder } = await supabase
    .from('orders')
    .select('*, order_items(*)')
    .eq('id', order.id)
    .single();

  // The cart is NOT cleared here: this row is still unpaid. Clearing it now
  // means a dismissed Razorpay modal leaves the customer with an empty cart
  // on the next reload. script.js clears it after payment succeeds.
  return { order: finalOrder ?? order };
}

export async function fetchMyOrders() {
  const user = await getSessionUser();
  if (!user) return { data: [] };
  const { data, error } = await supabase
    .from('orders')
    .select('*, order_items(*)')
    .eq('user_id', user.id)
    // Only show orders the customer paid for. An abandoned checkout (modal
    // closed before paying) leaves an unpaid row that should never surface
    // in "My Orders". Refunds stay visible.
    .in('payment_status', ['paid', 'refunded'])
    .order('created_at', { ascending: false });
  return { data: data ?? [], error };
}
